// AI reservation assistant — a chat modal powered by Claude (via the
// assistant-chat edge function). The assistant can explore services, check
// availability, and create holds through tools; it then instructs the
// widget to add items to the cart and hand off to the existing checkout
// flow. PII entry and payment are always done by the customer in the
// existing CartPanel — the AI never touches them.

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCart } from "../state/cart";
import { getCartToken } from "../state/booking";
import {
  invokeAssistant,
  type AssistantClientToolCall,
  type AssistantRequestMessage,
} from "../state/assistant";

type ChatMessage =
  | { role: "user"; text: string }
  | { role: "assistant"; text: string; handoff?: "request_details" | "open_checkout" | null }
  | { role: "system"; text: string };

export function AssistantModal({
  tenantSlug,
  widgetSlug,
  onClose,
  onOpenCheckout,
}: {
  tenantSlug: string;
  widgetSlug: string;
  onClose: () => void;
  onOpenCheckout: () => void;
}) {
  const { add: addToCart } = useCart();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      text: "Hi — tell me what you'd like to book and I'll take care of it. For example: \"a table for 4 on Friday at 7pm\".",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Full message history we send to the edge function. This mirrors
  // `messages` above but in the shape Claude expects (role + content).
  const historyRef = useRef<AssistantRequestMessage[]>([]);
  const conversationId = useMemo(
    () =>
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `conv-${Math.random().toString(36).slice(2)}-${Date.now()}`,
    [],
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Keep the transcript scrolled to the latest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const applyClientTools = useCallback(
    (calls: AssistantClientToolCall[]): "request_details" | "open_checkout" | null => {
      let handoff: "request_details" | "open_checkout" | null = null;
      for (const call of calls) {
        if (call.type === "add_to_cart") {
          const i = call.input;
          addToCart({
            serviceId: i.service_id,
            serviceName: i.service_name,
            serviceType: i.service_type ?? "reservation",
            unitId: null,
            unitName: null,
            description: i.description,
            quantity: 1,
            unitPrice: typeof i.amount_cents === "number" ? i.amount_cents / 100 : null,
            currency: i.currency,
            brandId: null,
            brandName: null,
            metadata: {
              booking_id: i.booking_id,
              requires_full_payment: i.requires_full_payment ?? false,
              amount_cents: i.amount_cents,
              starts_at: i.starts_at,
              ends_at: i.ends_at,
              party_size: i.party_size,
              source: "ai_assistant",
            },
          });
        } else if (call.type === "open_checkout") {
          handoff = "open_checkout";
        } else if (call.type === "request_details") {
          handoff = handoff ?? "request_details";
        }
      }
      return handoff;
    },
    [addToCart],
  );

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError(null);
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    historyRef.current.push({ role: "user", content: text });
    setSending(true);

    const resp = await invokeAssistant({
      tenantSlug,
      widgetSlug,
      conversationId,
      cartToken: getCartToken(),
      messages: historyRef.current,
    });

    if (!resp.ok) {
      setSending(false);
      setError(friendlyError(resp.error));
      // Roll the user turn back off the history so retry sends a clean
      // follow-up — Claude never saw it land on its side.
      historyRef.current.pop();
      return;
    }

    // Adopt the server's full content-block history (tool_use/tool_result
    // included) so next turn Claude remembers what it checked and held —
    // flattening to plain text here used to make every follow-up re-run the
    // whole tool dance (repeated "Let me check…" narration) and risk a
    // duplicate hold because the prior booking_id was invisible to it.
    if (resp.history && resp.history.length > 0) {
      historyRef.current = resp.history;
    } else if (resp.reply) {
      // Older function build without `history` — degrade to text-only memory.
      historyRef.current.push({ role: "assistant", content: resp.reply });
    }

    const handoff = applyClientTools(resp.tool_calls);

    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        text: resp.reply || "Done — check your booking.",
        handoff,
      },
    ]);
    setSending(false);
  }, [applyClientTools, conversationId, input, sending, tenantSlug, widgetSlug]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void send();
      }
    },
    [send],
  );

  return (
    <div className="vt-cart-backdrop" onClick={onClose}>
      <aside
        className="vt-cart-panel vt-assistant-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Reservation assistant"
      >
        <div className="vt-cart-header">
          <h3>Reservation assistant</h3>
          <button
            type="button"
            className="vt-cart-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="vt-assistant-log" ref={scrollRef}>
          {messages.map((m, idx) => (
            <MessageBubble
              key={idx}
              message={m}
              onOpenCheckout={onOpenCheckout}
              onClose={onClose}
            />
          ))}
          {sending && (
            <div className="vt-assistant-bubble vt-assistant-bubble-assistant vt-assistant-typing">
              <span />
              <span />
              <span />
            </div>
          )}
          {error && <div className="vt-assistant-error">{error}</div>}
        </div>

        <form
          className="vt-assistant-input"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <textarea
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask for a table, room, or event booking…"
            disabled={sending}
          />
          <button type="submit" disabled={sending || !input.trim()}>
            Send
          </button>
        </form>
      </aside>
    </div>
  );
}

function MessageBubble({
  message,
  onOpenCheckout,
  onClose,
}: {
  message: ChatMessage;
  onOpenCheckout: () => void;
  onClose: () => void;
}) {
  if (message.role === "system") {
    return <div className="vt-assistant-system">{message.text}</div>;
  }
  return (
    <div
      className={`vt-assistant-bubble vt-assistant-bubble-${message.role}`}
    >
      <p>{message.text}</p>
      {message.role === "assistant" && message.handoff && (
        <button
          type="button"
          className="vt-assistant-handoff"
          onClick={() => {
            onClose();
            onOpenCheckout();
          }}
        >
          {message.handoff === "request_details"
            ? "Enter your details"
            : "Review & pay"}
        </button>
      )}
    </div>
  );
}

function friendlyError(code: string): string {
  switch (code) {
    case "tier_not_eligible":
      return "The AI assistant isn't available on this plan.";
    case "no_credit":
      return "The AI assistant is temporarily unavailable. Please use the booking form below.";
    case "server_not_configured":
      return "The assistant isn't configured yet. Please try the booking form.";
    case "upstream_error":
      return "The assistant is having trouble right now. Try again in a moment.";
    default:
      return "Something went wrong. Please try again.";
  }
}
